import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { getPlatformAPI } from '../lib/platform'

export type DisplayMode =
  // macOS
  | 'dock-only' | 'menubar-only' | 'both-mac'
  // Windows
  | 'taskbar-only' | 'systray-only' | 'both-win'

interface PlatformDisplayModes {
  modes: DisplayMode[]
  defaultMode: DisplayMode
}

interface DisplayModeProviderContextProps {
  displayMode: DisplayMode
  platformModes: PlatformDisplayModes
  setDisplayMode: (mode: DisplayMode) => void
  isLoading: boolean
}

/** Exportiert für Stories mit festem Anzeigemodus (`stories/desktop.tsx`) — der Provider fragte den Main-Prozess. */
export const DisplayModeProviderContext = createContext<DisplayModeProviderContextProps | undefined>(undefined)

export function DisplayModeProvider({ children }: { children: React.ReactNode }) {
  const platformAPI = getPlatformAPI()
  const [displayMode, setDisplayModeState] = useState<DisplayMode>('dock-only')
  const [platformModes, setPlatformModes] = useState<PlatformDisplayModes>({
    modes: ['dock-only', 'menubar-only', 'both-mac'],
    defaultMode: 'dock-only'
  })
  const [isLoading, setIsLoading] = useState(true)

  const setDisplayMode = useCallback(async (mode: DisplayMode) => {
    try {
      if (!platformAPI.setDisplayMode) throw new Error('Display modes exist only in Electron')
      await platformAPI.setDisplayMode(mode)
      setDisplayModeState(mode)
      localStorage.setItem('displayMode', mode)
    } catch (error) {
      console.error('[DisplayModeProvider] Failed to set display mode:', error)
    }
  }, [platformAPI])

  // Listen for display mode changes from other windows (Electron only).
  // Uses a functional setter so the listener reads the latest state without
  // re-attaching on every displayMode change.
  useEffect(() => {
    const handleDisplayModeChange = (mode: DisplayMode) => {
      setDisplayModeState((prev) => {
        if (prev === mode) return prev
        localStorage.setItem('displayMode', mode)
        return mode
      })
    }

    if (window.api) {
      window.api.receive('display-mode-changed', handleDisplayModeChange)

      return () => {
        window.api.removeAllListeners('display-mode-changed')
      }
    }
    return undefined
  }, [])

  // Initialize display mode and platform modes on mount
  useEffect(() => {
    const initialize = async () => {
      try {
        setIsLoading(true)

        // Get platform-specific display modes (Electron only)
        if (window.api) {
          const modes = await window.api.invoke('get-platform-display-modes')
          setPlatformModes(modes as PlatformDisplayModes)

          // Get current display mode
          const currentMode = await platformAPI.getDisplayMode!()
          setDisplayModeState(currentMode as DisplayMode)
        }
      } catch (error) {
        console.error('[DisplayModeProvider] Failed to initialize display mode:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initialize()
  }, [platformAPI])

  const value = useMemo(() => ({
    displayMode,
    platformModes,
    setDisplayMode,
    isLoading
  }), [displayMode, platformModes, setDisplayMode, isLoading])

  return (
    <DisplayModeProviderContext.Provider value={value}>
      {children}
    </DisplayModeProviderContext.Provider>
  )
}

export function useDisplayMode() {
  const context = useContext(DisplayModeProviderContext)
  if (context === undefined) {
    throw new Error('useDisplayMode must be used within a DisplayModeProvider')
  }
  return context
}